import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
} from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import { toast } from "react-hot-toast";
import useRectorCommentService from "../../api/services/RectorCommentService";
import * as yup from "yup";

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, maxLength = 10000000, textAreaId = 'rich-text-area' }) => {
  const [formats, setFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    align: 'left',
    list: 'none'
  });

  const handleFormatChange = (format, value) => {
    setFormats(prev => ({ ...prev, [format]: value }));
    
    // Apply formatting to selected text
    const textarea = document.getElementById(textAreaId);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      if (selectedText) {
        let formattedText = selectedText;
        
        if (format === 'bold') {
          formattedText = value ? `<b>${selectedText}</b>` : selectedText.replace(/<\/?b>/g, '');
        } else if (format === 'italic') {
          formattedText = value ? `<i>${selectedText}</i>` : selectedText.replace(/<\/?i>/g, '');
        } else if (format === 'underline') {
          formattedText = value ? `<u>${selectedText}</u>` : selectedText.replace(/<\/?u>/g, '');
        } else if (format === 'strikethrough') {
          formattedText = value ? `<s>${selectedText}</s>` : selectedText.replace(/<\/?s>/g, '');
        }
        
        const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
        onChange(newValue);
      }
    }
  };

  const handleListFormat = (listType) => {
    const textarea = document.getElementById(textAreaId);
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      if (selectedText) {
        let formattedText = selectedText;
        const lines = selectedText.split('\n');
        
        if (listType === 'bullet') {
          formattedText = lines.map(line => line.trim() ? `• ${line}` : line).join('\n');
        } else if (listType === 'number') {
          formattedText = lines.map((line, index) => line.trim() ? `${index + 1}. ${line}` : line).join('\n');
        }
        
        const newValue = textarea.value.substring(0, start) + formattedText + textarea.value.substring(end);
        onChange(newValue);
      }
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 1, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" value={formats.bold} exclusive onChange={(e, value) => handleFormatChange('bold', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Bold">
              <FormatBold />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.italic} exclusive onChange={(e, value) => handleFormatChange('italic', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Italic">
              <FormatItalic />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.underline} exclusive onChange={(e, value) => handleFormatChange('underline', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Underline">
              <FormatUnderlined />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <ToggleButtonGroup size="small" value={formats.strikethrough} exclusive onChange={(e, value) => handleFormatChange('strikethrough', value)}>
          <ToggleButton value={true}>
            <Tooltip title="Strikethrough">
              <FormatStrikethrough />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Box sx={{ borderLeft: '1px solid #ccc', height: '24px', mx: 1 }} />
        
        <ToggleButtonGroup size="small" value={formats.align} exclusive onChange={(e, value) => setFormats(prev => ({ ...prev, align: value }))}>
          <ToggleButton value="left">
            <Tooltip title="Align Left">
              <FormatAlignLeft />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="center">
            <Tooltip title="Align Center">
              <FormatAlignCenter />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="right">
            <Tooltip title="Align Right">
              <FormatAlignRight />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Box sx={{ borderLeft: '1px solid #ccc', height: '24px', mx: 1 }} />
        
        <IconButton size="small" onClick={() => handleListFormat('bullet')}>
          <Tooltip title="Bullet List">
            <FormatListBulleted />
          </Tooltip>
        </IconButton>
        
        <IconButton size="small" onClick={() => handleListFormat('number')}>
          <Tooltip title="Numbered List">
            <FormatListNumbered />
          </Tooltip>
        </IconButton>
      </Paper>
      
      {/* Text Area */}
      <TextField
        id={textAreaId}
        fullWidth
        multiline
        rows={8}
        value={value}
        onChange={(e) => {
          const inputValue = e.target.value;
          // Strip HTML tags to count actual text content
          const textContent = inputValue.replace(/<[^>]*>/g, '');
          if (textContent.length <= maxLength) {
            onChange(inputValue);
          } else {
            toast.warning("Text content exceeds character limit");
          }
        }}
        placeholder={placeholder}
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.5',
          }
        }}
      />
    </Box>
  );
};

// Validation schema
const EditSchema = yup.object().shape({
  rectorComments: yup.string().test(
    'max-length',
    'Rector comments must be 10,000,000 characters or less',
    function(value) {
      if (!value) return true;
      const textContent = value.replace(/<[^>]*>/g, '');
      return textContent.length <= 10000000;
    }
  ),
});

const EditDialog = ({ open, onClose, onSuccess, editingData, terms }) => {
  const { updateRectorCommentByTermStudent, updatePartialRectorCommentByTermStudent } = useRectorCommentService();
  const [isLoading, setIsLoading] = useState(false);

  const getInitialValues = () => {
    if (!editingData) {
      return {
        rectorComments: "",
      };
    }

    return {
      rectorComments: editingData.rectorComments || "",
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!editingData || !editingData.originalData) {
      toast.error("No data to update");
      setSubmitting(false);
      return;
    }

    try {
      setIsLoading(true);
      const originalData = editingData.originalData;
      // Use the original termId from the selected row (fixed, not editable)
      const termId = originalData.termId || originalData.term?.id;
      const studentId = originalData.studentId || originalData.student?.id;

      if (!termId || !studentId) {
        toast.error("Term and Student information is required");
        setSubmitting(false);
        setIsLoading(false);
        return;
      }

      // Prepare DTO - always include both fields, even if empty
      const dto = {
        rectorComments: values.rectorComments && values.rectorComments.trim() ? values.rectorComments : null,
      };

      // Use partial update to only update provided fields
      await updatePartialRectorCommentByTermStudent(termId, studentId, dto, () => {
        toast.success("Rector comment updated successfully");
        onSuccess?.();
        onClose();
      });
    } catch (error) {
      console.error("Error updating rector comment:", error);
      toast.error("Failed to update rector comment: " + (error.response?.data?.message || error.message || "Unknown error"));
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  if (!editingData) {
    return null;
  }

  // Get term name for display
  const termName = editingData.term?.termName || editingData.termName || "N/A";
  const studentName = editingData.originalData?.student?.studentName || editingData.originalData?.studentName || "N/A";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Rector Comment
        <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '8px', color: '#666' }}>
          Term: <strong>{termName}</strong> | Student: <strong>{studentName}</strong>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={EditSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={2} mt={1}>
                {/* Rector comments */}
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Rector Comments
                    </Typography>
                    <RichTextEditor
                      value={values.rectorComments || ''}
                      onChange={(content) => setFieldValue("rectorComments", content)}
                      placeholder="Enter rector's comments about the student... (Max 10,000,000 characters)"
                      maxLength={10000000}
                      textAreaId="edit-rector-comments-area"
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="caption" 
                        color={touched.rectorComments && errors.rectorComments ? 'error' : 'text.secondary'}
                      >
                        {touched.rectorComments && errors.rectorComments 
                          ? errors.rectorComments 
                          : (() => {
                              const textContent = (values.rectorComments || '').replace(/<[^>]*>/g, '');
                              const length = textContent.length;
                              const remaining = 10000000 - length;
                              if (remaining < 100000) {
                                return `⚠️ ${length.toLocaleString()}/10,000,000 characters (${remaining.toLocaleString()} remaining)`;
                              }
                              return `${length.toLocaleString()}/10,000,000 characters`;
                            })()
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting || isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="secondary" disabled={isSubmitting || isLoading}>
                  {isSubmitting || isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Updating...
                    </Box>
                  ) : (
                    "Update"
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditDialog;

